import { MemoryFileSystem } from '@playcanvas/splat-transform';

import { Events } from './events';
import { Scene } from './scene';
import { Splat } from './splat';
import { writeSplatFile } from './splat-serialize';
import { i18n } from './ui/localization';

// merge all visible splat elements into a single one.
//
// the editor renders each splat element with its own depth sorter, so
// transparency between overlapping elements composes in draw order rather
// than true depth order (upstream renders with `unified: false`). merging
// bakes every element - world transform included - into one element with one
// global sort, which displays and exports correctly. exported files were
// always merged; this makes the in-editor display match.
const registerMergeHandler = (events: Events, scene: Scene) => {
    let mergeCounter = 0;

    events.function('scene.mergeSplats', async () => {
        const splats = events.invoke('scene.splats') as Splat[];

        if (splats.length < 2) {
            await events.invoke('showPopup', {
                type: 'info',
                header: i18n.t('popup.merge.header'),
                message: i18n.t('popup.merge.too-few')
            });
            return null;
        }

        // merging removes the source elements, which also drops their undo
        // history - ask first
        const result = await events.invoke('showPopup', {
            type: 'yesno',
            header: i18n.t('popup.merge.header'),
            message: i18n.t('popup.merge.message', { count: splats.length })
        });
        if (result?.action !== 'yes') {
            return null;
        }

        events.fire('startSpinner');
        try {
            // serialize all elements to an in-memory PLY. this reuses the
            // export pipeline, so world transforms are baked and deleted
            // splats are dropped, exactly like a file export
            const filename = `${i18n.t('popup.merge.name')} ${++mergeCounter}.ply`;
            const memFs = new MemoryFileSystem();
            await writeSplatFile(splats, { maxSHBands: 3 }, 'ply', filename, {}, memFs);
            const bytes = memFs.results.get(filename);
            if (!bytes) {
                throw new Error('serializing failed');
            }

            // permanently remove the source elements (like scene panel delete)
            splats.forEach((splat) => {
                scene.remove(splat);
                splat.destroy();
            });

            // import the combined data as a single new element
            const models = await events.invoke('import', [{
                filename,
                contents: new File([bytes as BlobPart], filename)
            }]) as Splat[];

            const merged = models?.[0] ?? null;
            if (merged) {
                events.fire('selection', merged);
            }
            return merged;
        } finally {
            events.fire('stopSpinner');
        }
    });
};

export { registerMergeHandler };
